import { CDropdown, CDropdownMenu, CSpinner } from "@coreui/react"
import downArrow from "assets/icons/downArrow.svg"
import classNames from "classnames"
import { CMSButton } from "components/common/button"
import AccountProvider from "components/common/Context/AccountContext"
import ToasterProvider from "components/common/Context/ToasterContext"
import { Tooltip } from "components/common/tooltip"
import { accountSelect } from "constants/StringConstants"
import PropTypes from "prop-types"
import React, { useContext, useEffect, useRef, useState } from "react"

function AccountsSelectDropdown({
  isLoading,
  mobileView,
  disabled,
  isAccountSelectDropdownOpen,
  setIsAccountSelectDropdownOpen,
  isProjectFailed
}) {
  const dropdownRef = useRef(null)
  const enqueueToast = useContext(ToasterProvider)

  const {
    accounts,
    selectedAccounts,
    setSelectedAccounts,
    setIsEmptyAccSelected
  } = useContext(AccountProvider)

  const [selectedAccountNos, setSelectedAccountNos] = useState(
    selectedAccounts.map((eachAcc) => eachAcc.account_number)
  )

  // to handle dropdown autoclose when clicked outside the folder select dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsAccountSelectDropdownOpen(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [dropdownRef, setIsAccountSelectDropdownOpen])

  // for selecting accounts. At least 1 account will always be selected
  const handleSelect = (itemVal, e) => {
    e.preventDefault()
    if (itemVal === "All accounts") {
      if (selectedAccountNos.length === accounts.length)
        setSelectedAccountNos([])
      else setSelectedAccountNos(accounts.map((item) => item.account_number))
    } else {
      if (
        selectedAccountNos.length !== 1 ||
        !selectedAccountNos.includes(itemVal)
      ) {
        setSelectedAccountNos(
          selectedAccountNos.includes(itemVal)
            ? selectedAccountNos.filter((item) => item !== itemVal)
            : [...selectedAccountNos, itemVal]
        )
      } else {
        enqueueToast("failure", null, "You can't unselect all accounts")
      }
    }
  }

  const handleChange = () => {
    if (selectedAccountNos.length === 0)
      enqueueToast("failure", null, "You can't unselect all accounts")
    else {
      setIsEmptyAccSelected(
        Boolean(selectedAccountNos.find((eachAcc) => eachAcc === "EMPTY"))
      )
      setSelectedAccounts(
        selectedAccountNos.map((selectedKey) =>
          accounts.find(
            (accountObj) => accountObj.account_number === selectedKey
          )
        )
      )
      setIsAccountSelectDropdownOpen(false)
    }
  }

  const handleCancel = () => {
    setIsAccountSelectDropdownOpen(false)
  }

  const dropdownItem = (accountObj) => (
    <p
      className="acc-checkbox w-100m pt-1"
      tabIndex={0}
      onClick={(e) => handleSelect(accountObj.account_number, e)}
      title={
        accountObj.account_environment === "Unassigned"
          ? "The 'Unassigned' account serves \n as a catchall for new incidents\n and change requests that have \nnot yet been associated with \n a specific account."
          : `${accountObj.account_environment}\n${accountObj.account_name}\n${accountObj.account_number}`
      }
    >
      <input
        type="checkbox"
        id={accountObj.account_number}
        name="account"
        checked={selectedAccountNos.includes(accountObj.account_number)}
        disabled={
          selectedAccountNos.length === 1 &&
          selectedAccountNos.includes(accountObj.account_number)
        }
        readOnly
      />
      <label htmlFor={accountObj.account_number} className="mt-0 pt-0">
        <span className="font-weight-bold account-environment">{`${accountObj.account_environment}`}</span>
        <span className="account-name">{`${accountObj.account_name}`}</span>
        <span className="account-id">
          {accountObj.account_number === "EMPTY"
            ? "Unassigned"
            : accountObj.account_number}
        </span>
      </label>
    </p>
  )

  // for rendering cancel okay buttons
  const actionButtons = () => (
    <div className="d-flex justify-content-end m-1">
      <div className="mr-2">
        <CMSButton outline type="primary" onClick={handleCancel}>
          <div style={{ width: "4.375rem" }}>Cancel</div>
        </CMSButton>
      </div>
      <div className="mr-2">
        <CMSButton type="primary" onClick={handleChange} dataTestId="apply-btn">
          <div style={{ width: "4.375rem" }}>Okay</div>
        </CMSButton>
      </div>
    </div>
  )

  return (
    <>
      <CDropdown
        className="account-dropdown project-select-dropdown-width"
        innerRef={dropdownRef}
      >
        <CMSButton
          type="secondary"
          disabled={isLoading || disabled}
          onClick={() => {
            setSelectedAccountNos(
              selectedAccounts.map((eachAcc) => eachAcc.account_number)
            )
            setIsAccountSelectDropdownOpen(true)
          }}
          dataTestId="acc-select-btn2"
          className="w-100"
        >
          {isLoading ? (
            isProjectFailed && (
              <div className="w-100 d-flex justify-content-center">
                <CSpinner size="sm" />
              </div>
            )
          ) : (
            <>
              <span className="text-ellipsis">
                {!selectedAccounts.length
                  ? "No Accounts Selected"
                  : selectedAccounts.length === accounts.length
                  ? "All Accounts"
                  : `${selectedAccounts.length} Account(s) ${
                      mobileView ? "" : "Selected"
                    }`}
              </span>
              <img
                src={downArrow}
                alt="down arrow"
                className="iconDropdownStyle"
              />
            </>
          )}
        </CMSButton>

        <CDropdownMenu
          className={classNames("w-100 py-0 mobile-view-width-account", {
            show: isAccountSelectDropdownOpen
          })}
        >
          <div className="dropdown-scrollbar acc-dd-height overflow-auto ">
            <div className="width-max-content min-width-100">
              <div className="dropdown-item py-0 px-3 ">
                <p
                  className="select-all-checkbox pt-2 "
                  onClick={(e) => handleSelect("All accounts", e)}
                >
                  <input
                    type="checkbox"
                    id="all_acc"
                    name="account"
                    checked={
                      selectedAccountNos.length === accounts.length &&
                      selectedAccountNos.length !== 0
                    }
                    readOnly
                  />
                  <label>
                    <b>Select all Accounts</b>
                  </label>
                </p>
              </div>
              {accounts.map((accountObj) => (
                <div
                  className="dropdown-item  py-0 px-3"
                  key={accountObj.account_number}
                >
                  {accountObj.account_number === "EMPTY"
                    ? dropdownItem(accountObj)
                    : dropdownItem(accountObj)}
                </div>
              ))}
            </div>
          </div>

          {actionButtons()}
        </CDropdownMenu>
      </CDropdown>
      <Tooltip className="ml-2" heading="Select Account" text={accountSelect} />
    </>
  )
}

AccountsSelectDropdown.defaultProps = {
  isLoading: false,
  mobileView: false,
  disabled: false,
  isAccountSelectDropdownOpen: false,
  setIsAccountSelectDropdownOpen: () => {},
  isProjectFailed: false
}

AccountsSelectDropdown.propTypes = {
  isLoading: PropTypes.bool,
  mobileView: PropTypes.bool,
  disabled: PropTypes.bool,
  isAccountSelectDropdownOpen: PropTypes.bool,
  setIsAccountSelectDropdownOpen: PropTypes.func,
  isProjectFailed: PropTypes.bool
}

export default AccountsSelectDropdown
